<template>
  <div>
    <b-card class="p-0 filter-card rounded-lg">
      <b-row>
        <b-col
          cols="12"
          class="d-flex justify-content-between align-items-center mb-3"
        >
          <h3 class="ml-1 mb-0">
            ملفات RW5
          </h3>
          <div>
            <b-button
              variant="success"
              class="mr-2"
              @click="$refs.uploadModal.show()"
            >
              <feather-icon
                icon="UploadIcon"
                size="14"
                class="mr-1"
              />
              رفع ملف ZIP
            </b-button>
            <b-button
              variant="primary"
              class="mr-2"
              :disabled="selectedFiles.length !== 1"
              @click="showAssignModal"
            >
              <feather-icon
                icon="LinkIcon"
                size="14"
                class="mr-1"
              />
              ربط بمشروع
            </b-button>
            <b-button
              variant="warning"
              :disabled="selectedFiles.length === 0 || marking"
              @click="markAsMoved"
            >
              <feather-icon
                icon="CheckIcon"
                size="14"
                class="mr-1"
              />
              {{ marking ? 'جاري التحديث...' : `تعليم كمنقول (${selectedFiles.length})` }}
            </b-button>
          </div>
        </b-col>

        <!-- Filters -->
        <b-col
          cols="12"
          md="3"
        >
          <b-form-input
            v-model="filters.search"
            placeholder="بحث باسم الملف"
            @input="handleSearch"
          />
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.status"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="statusOptions"
            :reduce="(val) => val.value"
            placeholder="الحالة"
            label="label"
            @input="handleFilterChange"
          >
            <template #option="{ label }">
              <span>{{ label }}</span>
            </template>
            <template #selected-option="{ label }">
              <span>{{ label }}</span>
            </template>
          </v-select>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-form-input
            v-model="filters.project_id"
            type="number"
            placeholder="رقم المشروع"
            @input="handleFilterChange"
          />
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.sort_by"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="sortByOptions"
            :reduce="(val) => val.value"
            placeholder="ترتيب حسب"
            label="label"
            @input="handleFilterChange"
          >
            <template #option="{ label }">
              <span>{{ label }}</span>
            </template>
            <template #selected-option="{ label }">
              <span>{{ label }}</span>
            </template>
          </v-select>
        </b-col>
      </b-row>
    </b-card>

    <!-- Table -->
    <b-card class="mt-1">
      <b-spinner
        v-if="loading"
        class="d-block text-center my-4"
      />
      <div
        v-else-if="error"
        class="alert alert-danger"
      >
        {{ error }}
      </div>
      <b-table
        v-else
        ref="table"
        :items="files"
        :fields="fields"
        responsive
        striped
        hover
        :per-page="pagination && pagination.per_page ? pagination.per_page : 15"
        :current-page="pagination && pagination.current_page ? pagination.current_page : 1"
        selectable
        select-mode="multi"
        @row-selected="onRowSelected"
      >
        <template #head(selected)>
          <b-form-checkbox
            :checked="allSelected"
            :indeterminate="indeterminate"
            @change="toggleSelectAll"
          />
        </template>
        <template #cell(selected)="{ rowSelected }">
          <b-form-checkbox
            :checked="rowSelected"
            @change="() => {}"
          />
        </template>

        <template #cell(file_name)="row">
          <strong>{{ row.item.file_name }}</strong>
        </template>

        <template #cell(status)="row">
          <b-badge :variant="getStatusVariant(row.item.status)">
            {{ getStatusLabel(row.item.status) }}
          </b-badge>
        </template>

        <template #cell(project_name)="row">
          <span v-if="row.item.project_name">{{ row.item.project_name }}</span>
          <span
            v-else
            class="text-muted"
          >-</span>
        </template>

        <template #cell(points_count)="row">
          {{ row.item.points_count || 0 }}
        </template>

        <template #cell(assigned_at)="row">
          <span v-if="row.item.assigned_at">
            {{ formatDate(row.item.assigned_at) }}
          </span>
          <span
            v-else
            class="text-muted"
          >-</span>
        </template>

        <template #cell(actions)="row">
          <b-button
            variant="info"
            size="sm"
            class="mr-1"
            @click="viewDetails(row.item)"
          >
            <feather-icon
              icon="EyeIcon"
              size="14"
            />
          </b-button>
          <b-button
            v-if="row.item.status === 'pending'"
            variant="primary"
            size="sm"
            @click="assignToProject(row.item)"
          >
            <feather-icon
              icon="LinkIcon"
              size="14"
            />
          </b-button>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-pagination
        v-if="pagination && pagination.last_page > 1"
        v-model="filters.page"
        :total-rows="pagination.total"
        :per-page="pagination.per_page"
        align="center"
        class="mt-3"
        @change="onPageChange"
      />
    </b-card>

    <!-- Upload Modal -->
    <upload-rw5-zip
      ref="uploadModal"
      @uploaded="handleUploadSuccess"
    />

    <!-- Assign Modal -->
    <b-modal
      id="assign-modal"
      v-model="showAssignDialog"
      title="ربط ملف RW5 بمشروع"
      @ok="confirmAssign"
      @cancel="resetAssignForm"
    >
      <b-form-group label="كود مساحي *">
        <b-form-input
          v-model="assignForm.survey_coding"
          type="text"
          placeholder="أدخل كود مساحي"
        />
      </b-form-group>
      <div
        v-if="assignError"
        class="alert alert-danger mt-2"
      >
        {{ assignError }}
      </div>
    </b-modal>

    <!-- Details Modal -->
    <b-modal
      id="details-modal"
      v-model="showDetailsModal"
      title="تفاصيل الملف"
      size="lg"
      ok-only
      ok-title="إغلاق"
    >
      <div v-if="selectedFileDetails">
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <p><strong>اسم الملف:</strong> {{ selectedFileDetails.file_name }}</p>
            <p><strong>الحالة:</strong>
              <b-badge :variant="getStatusVariant(selectedFileDetails.status)">
                {{ getStatusLabel(selectedFileDetails.status) }}
              </b-badge>
            </p>
            <p><strong>عدد النقاط:</strong> {{ selectedFileDetails.points_count || 0 }}</p>
            <p v-if="selectedFileDetails.project_name">
              <strong>المشروع:</strong> {{ selectedFileDetails.project_name }}
            </p>
            <p v-if="selectedFileDetails.assigned_at">
              <strong>تاريخ الربط:</strong> {{ formatDate(selectedFileDetails.assigned_at) }}
            </p>
          </b-col>
        </b-row>
        <hr v-if="selectedFileDetails.coordinates && selectedFileDetails.coordinates.length > 0">
        <div v-if="selectedFileDetails.coordinates && selectedFileDetails.coordinates.length > 0">

          <h5>الإحداثيات:</h5>
          <b-table
            :items="selectedFileDetails.coordinates"
            :fields="coordinateFields"
            small
            striped
            responsive
          >
            <template #cell(latitude)="row">
              {{ Number(row.item.latitude).toFixed(7) }}
            </template>
            <template #cell(longitude)="row">
              {{ Number(row.item.longitude).toFixed(7) }}
            </template>
            <template #cell(utm_east)="row">
              {{ Number(row.item.utm_east).toFixed(4) }}
            </template>
            <template #cell(utm_north)="row">
              {{ Number(row.item.utm_north).toFixed(4) }}
            </template>
            <template #cell(elevation)="row">
              {{ Number(row.item.elevation).toFixed(2) }}
            </template>
          </b-table>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BSpinner,
  BPagination,
  BFormInput,
  BFormCheckbox,
  BBadge,
  BModal,
  BFormGroup,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import geodesiaApi from '@/api/geodesia/geodesia'
import UploadRw5Zip from './UploadRw5Zip.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BSpinner,
    BPagination,
    BFormInput,
    BBadge,
    BFormCheckbox,
    BModal,
    BFormGroup,
    vSelect,
    FeatherIcon,
    UploadRw5Zip,
  },
  data() {
    return {
      files: [],
      selectedFiles: [],
      selectedRows: [],
      loading: false,
      marking: false,
      error: null,
      pagination: null,
      showAssignDialog: false,
      showDetailsModal: false,
      selectedFileDetails: null,
      assignForm: {
        survey_coding: null,
      },
      assignError: null,
      filters: {
        search: '',
        status: null,
        project_id: null,
        sort_by: 'created_at',
        sort_order: 'desc',
        per_page: 15,
        page: 1,
      },
      fields: [
        { key: 'selected', label: '', thStyle: { width: '40px' } },
        { key: 'file_name', label: 'اسم الملف', sortable: true },
        { key: 'status', label: 'الحالة' },
        { key: 'project_name', label: 'المشروع' },
        { key: 'points_count', label: 'عدد النقاط' },
        { key: 'assigned_at', label: 'تاريخ الربط' },
        { key: 'actions', label: 'الإجراءات', thStyle: { width: '120px' } },
      ],
      coordinateFields: [
        { key: 'point_number', label: 'رقم النقطة' },
        { key: 'latitude', label: 'خط العرض' },
        { key: 'longitude', label: 'خط الطول' },
        { key: 'utm_east', label: 'UTM East' },
        { key: 'utm_north', label: 'UTM North' },
        { key: 'utm_zone', label: 'المنطقة' },
        { key: 'elevation', label: 'الارتفاع' },
      ],
      statusOptions: [
        { value: 'pending', label: 'قيد الانتظار' },
        { value: 'assigned', label: 'مربوط' },
        { value: 'moved', label: 'منقول' },
      ],
      sortByOptions: [
        { value: 'created_at', label: 'تاريخ الإنشاء' },
        { value: 'file_name', label: 'اسم الملف' },
        { value: 'status', label: 'الحالة' },
      ],
    }
  },
  computed: {
    allSelected() {
      return this.files.length > 0 && this.selectedFiles.length === this.files.length
    },
    indeterminate() {
      return this.selectedFiles.length > 0 && this.selectedFiles.length < this.files.length
    },
  },
  mounted() {
    this.fetchFiles()
  },
  methods: {
    async fetchFiles() {
      this.loading = true
      this.error = null

      try {
        const params = { ...this.filters }
        // Remove null and empty values
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === '') {
            delete params[key]
          }
        })

        const response = await geodesiaApi.getRw5Files(params)

        if (response.success) {
          this.files = response.data || []
          this.pagination = response.meta || null
          // Clear selections when fetching new data
          this.selectedFiles = []
          this.selectedRows = []
          this.$nextTick(() => {
            if (this.$refs.table) {
              this.$refs.table.clearSelected()
            }
          })
        } else {
          this.error = response.message || 'حدث خطأ أثناء جلب البيانات'
        }
      } catch (err) {
        console.error('Error fetching RW5 files:', err)
        this.error = (err.response && err.response.data && err.response.data.message) || 'حدث خطأ أثناء جلب البيانات'
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.filters.page = 1
        this.fetchFiles()
      }, 500)
    },
    handleFilterChange() {
      this.filters.page = 1
      this.fetchFiles()
    },
    onPageChange(page) {
      this.filters.page = page
      this.fetchFiles()
    },
    onRowSelected(items) {
      this.selectedRows = items
      this.selectedFiles = items.map(item => item.id)
    },
    toggleSelectAll(checked) {
      if (checked) {
        this.selectAllRows()
      } else {
        this.clearSelected()
      }
    },
    selectAllRows() {
      if (this.$refs.table) {
        this.$refs.table.selectAllRows()
      }
    },
    clearSelected() {
      this.selectedFiles = []
      this.selectedRows = []
      if (this.$refs.table) {
        this.$refs.table.clearSelected()
      }
    },
    async markAsMoved() {
      if (this.selectedFiles.length === 0) {
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'تنبيه',
            icon: 'AlertCircleIcon',
            text: 'يرجى اختيار ملف واحد على الأقل',
            variant: 'warning',
          },
        })
        return
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `هل أنت متأكد من تعليم ${this.selectedFiles.length} ملف كمنقول؟`,
        {
          title: 'تأكيد',
          okVariant: 'primary',
          okTitle: 'نعم',
          cancelTitle: 'إلغاء',
          centered: true,
        },
      )

      if (!confirmed) return

      this.marking = true
      try {
        const response = await geodesiaApi.markRw5FilesAsMoved(this.selectedFiles)

        if (response.success) {
          this.$toast.success(`تم تعليم ${response.data.updated} ملف كمنقول بنجاح`)
          this.fetchFiles()
          this.clearSelected()
        } else {
          this.$toast.error(response.message || 'فشل تحديث الملفات')
        }
      } catch (err) {
        console.error('Error marking files as moved:', err)
        const errorMessage = (err.response && err.response.data && err.response.data.message) || 'فشل تحديث الملفات'
        this.$toast.error(errorMessage)
      } finally {
        this.marking = false
      }
    },
    showAssignModal() {
      if (this.selectedFiles.length !== 1) {
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'تنبيه',
            icon: 'AlertCircleIcon',
            text: 'يرجى اختيار ملف واحد فقط',
            variant: 'warning',
          },
        })
        return
      }
      this.assignForm.survey_coding = null
      this.assignError = null
      this.showAssignDialog = true
    },
    assignToProject(file) {
      this.selectedFiles = [file.id]
      this.showAssignModal()
    },
    async confirmAssign() {
      if (!this.assignForm.survey_coding) {
        this.assignError = 'يرجى إدخال كود مساحي'
        return
      }

      if (this.selectedFiles.length !== 1) {
        this.assignError = 'يرجى اختيار ملف واحد فقط'
        return
      }

      try {
        const response = await geodesiaApi.assignMediaToProjectBySurveyCoding(
          this.assignForm.survey_coding,
          this.selectedFiles[0],
        )

        if (response.success) {
          this.$toast.success(`تم ربط الملف بالمشروع بنجاح ${this.assignForm.survey_coding}`)
          this.showAssignDialog = false
          this.resetAssignForm()
          this.fetchFiles()
          this.clearSelected()
        } else {
          this.assignError = response.message || `فشل ربط الملف بالمشروع ${this.assignForm.survey_coding}`
        }
      } catch (err) {
        console.error('Error assigning file to project:', err)
        if (err.response && err.response.status === 422) {
          this.assignError = `هذا الملف مربوط بالفعل بهذا المشروع ${this.assignForm.survey_coding}`
        } else {
          this.assignError = (err.response && err.response.data && err.response.data.message) || 'فشل ربط الملف بالمشروع'
        }
      }
    },
    resetAssignForm() {
      this.assignForm.survey_coding = null
      this.assignError = null
    },
    viewDetails(file) {
      this.selectedFileDetails = file
      this.showDetailsModal = true
    },
    handleUploadSuccess() {
      this.fetchFiles()
    },
    getStatusLabel(status) {
      const labels = {
        pending: 'قيد الانتظار',
        assigned: 'مربوط',
        moved: 'منقول',
      }
      return labels[status] || status
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        assigned: 'success',
        moved: 'info',
      }
      return variants[status] || 'secondary'
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.filter-card {
  margin-bottom: 1rem;
}
</style>
